import { useState } from "react";
import { ChevronDown, Download, Loader2, Mail, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

const blobError = async (e) => {
  const d = e?.response?.data;
  if (d instanceof Blob) { try { return JSON.parse(await d.text()).detail || apiError(e); } catch { return apiError(e); } }
  return apiError(e);
};

async function fetchPdf(id, range, channel, to = "") {
  const res = await api.get(`/customers/${id}/statement.pdf`, { params: { start: range.from, end: range.to, channel, to }, responseType: "blob" });
  const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "Ekstre.pdf";
  return { blob: res.data, name };
}

function save({ blob, name }) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function EmailDialog({ id, range, info, onClose }) {
  const [to, setTo] = useState(info.email);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/customers/${id}/statement/email`, { to, start: range.from, end: range.to }); toast.success(`Ekstre ${to} adresine gönderildi`); onClose(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="statement-email-dialog">
        <DialogHeader><DialogTitle>Ekstreyi E-posta ile Gönder</DialogTitle></DialogHeader>
        <p className="text-[13px] text-slate-600">{info.period} · Bakiye {info.balance_side}</p>
        <select value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm" data-testid="statement-email-to">
          {info.allowed_recipients.map((e, i) => <option key={e} value={e}>{e}{i === 0 ? " (cari)" : " (şirket içi)"}</option>)}</select>
        <p className="text-[11px] text-slate-500">Yalnızca carinin kayıtlı e-postası veya şirket içi alıcılar seçilebilir.</p>
        <DialogFooter><Button disabled={busy || !info.email_enabled} onClick={send} data-testid="statement-email-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gönder"}</Button></DialogFooter>
        {!info.email_enabled && <p className="text-[12px] text-amber-700">E-posta gönderimi şu an kapalı.</p>}
      </DialogContent>
    </Dialog>
  );
}

// Ekstre "Paylaş ▾": PDF İndir · WhatsApp ile Paylaş · E-posta Gönder (yalnız carinin kayıtlı e-postası varsa). Seçili tarih aralığıyla.
export default function StatementShare({ id, range }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const [mail, setMail] = useState(false);
  const loadInfo = () => api.get(`/customers/${id}/statement/share-info`, { params: { start: range.from, end: range.to } }).then(({ data }) => { setInfo(data); return data; });
  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(await blobError(e)); } setBusy(""); };
  const whatsapp = async () => {
    const i = await loadInfo();
    const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const pdf = await fetchPdf(id, range, "whatsapp", i.whatsapp_phone || "");
    const file = new File([pdf.blob], pdf.name, { type: "application/pdf" });
    if (mobile && navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], text: i.whatsapp_text }); return; } catch (e) { if (e?.name === "AbortError") return; }
    }
    save(pdf);
    window.open(`https://wa.me/${i.whatsapp_phone || ""}?text=${encodeURIComponent(i.whatsapp_text)}`, "_blank");
    toast.info(i.whatsapp_phone ? "PDF indirildi, WhatsApp'ta dosyayı ekleyin" : "Carinin geçerli cep telefonu yok; WhatsApp'ta kişiyi seçip indirilen PDF'i ekleyin", { duration: 8000 });
  };
  return (
    <>
      <DropdownMenu modal={false} onOpenChange={(o) => o && !info && loadInfo().catch(() => {})}>
        <DropdownMenuTrigger asChild><Button type="button" size="sm" variant="outline" className="h-9" disabled={!!busy} data-testid="statement-share-button">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Share2 className="h-4 w-4 mr-1" />}Paylaş<ChevronDown className="h-3.5 w-3.5 ml-1" /></Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="statement-share-menu">
          <DropdownMenuItem onSelect={() => run("pdf", async () => save(await fetchPdf(id, range, "indir")))} data-testid="statement-share-pdf"><Download className="h-4 w-4 mr-2" />PDF İndir</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => run("wa", whatsapp)} className="text-emerald-700" data-testid="statement-share-whatsapp"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp ile Paylaş</DropdownMenuItem>
          {info?.email && <DropdownMenuItem onSelect={() => run("mail", async () => { await loadInfo(); setMail(true); })} data-testid="statement-share-email"><Mail className="h-4 w-4 mr-2" />E-posta Gönder</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
      {mail && info && <EmailDialog id={id} range={range} info={info} onClose={() => setMail(false)} />}
    </>
  );
}
